import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Calendar, LogOut } from 'lucide-react';
import { getLocalDateStringFormatted } from '../lib/utils';

export default function Profile() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-[#264653] font-outfit">Profile</h1>
          <p className="text-[#6B7280] mt-1">Manage your account settings</p>
        </div>

        {/* Profile Card */}
        <Card className="bg-white rounded-3xl border-[#D4AF37]/20 shadow-xl overflow-hidden">
          {/* Header Background */}
          <div className="h-32 bg-islamic-green" />
          
          <CardContent className="pt-0 -mt-16 px-8 pb-8">
            <div className="flex flex-col items-center">
              <Avatar className="h-32 w-32 border-4 border-white shadow-xl">
                <AvatarImage src={user?.picture} />
                <AvatarFallback className="text-4xl font-bold bg-[#08513F] text-white">
                  {user?.full_name?.charAt(0) || 'U'}
                </AvatarFallback>
              </Avatar>

              <h2 className="text-2xl font-bold text-[#264653] font-outfit mt-4">
                {user?.full_name}
              </h2>
              
              <Badge className={`mt-2 rounded-full px-4 py-1 ${
                user?.role === 'admin' 
                  ? 'bg-[#D4AF37] text-white' 
                  : 'bg-[#08513F]/10 text-[#08513F]'
              }`}>
                <Shield className="w-3 h-3 mr-1" />
                {user?.role === 'admin' ? 'Administrator' : 'Parent'}
              </Badge>
            </div>

            {/* Info */}
            <div className="mt-8 space-y-4">
              <div className="flex items-center gap-4 p-4 bg-[#FFFBEB] rounded-xl">
                <Mail className="w-5 h-5 text-[#08513F]" />
                <div>
                  <p className="text-sm text-[#6B7280]">Email</p>
                  <p className="font-medium text-[#264653]">{user?.email}</p>
                </div>
              </div>

              <div className="flex items-center gap-4 p-4 bg-[#FFFBEB] rounded-xl">
                <Calendar className="w-5 h-5 text-[#08513F]" />
                <div>
                  <p className="text-sm text-[#6B7280]">Member Since</p>
                  <p className="font-medium text-[#264653]">
                    {user?.created_at 
                      ? getLocalDateStringFormatted(user.created_at)
                      : 'N/A'}
                  </p>
                </div>
              </div>
            </div>

            {/* Logout */}
            <Button
              onClick={handleLogout}
              variant="outline"
              className="w-full mt-8 h-12 rounded-xl border-2 border-red-200 text-red-500 hover:bg-red-50 hover:border-red-300"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </Button>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
